'use client';

import { useCallback, useMemo, useState } from 'react';
import type { Message } from '@/components/chat/chat-types';
import { useUniversalChat } from '@/components/chat/universal-chat-context';
import { copyToClipboard } from '@/lib/utils/clipboard';
import { toTrimmedNonEmptyString } from '@/lib/utils/type-guards';
import type { ConversationThreadResult } from '../useConversationThread';
import { MAX_TWEETS_FOR_CHAT_CONTEXT } from './constants';
type ConversationThreadTweet = ConversationThreadResult['tweets'][string];
const toIsoOrNull = (value: Date | null | undefined): string | null => value ? value.toISOString() : null;
interface ConversationSummaryChatParameters {
  canonicalSummaryTweetId: string;
  conversation: {
    id: string;
    rootTweetId: string;
    tweetCount?: number | null;
    participantCount?: number | null;
    maxDepth?: number | null;
    lastActivityAt?: Date | null;
    dataSources?: unknown;
    isComplete?: boolean;
    missingTweets?: boolean;
  };
  resolvedConsideredTweetCount: number;
  resolvedImageCount: number;
  rootProfile: {
    username: string;
  } | null;
  summaryImages: Array<{
    url: string;
    altText?: string | null;
  }>;
  summaryText: string;
  thread: {
    tweetIds: ConversationThreadResult['tweetIds'];
    tweets: ConversationThreadResult['tweets'];
    profiles?: ConversationThreadResult['profiles'];
  };
  totalTweetCount: number;
}
interface ConversationContextTweet {
  authorId: string | null;
  authorName: string | null;
  authorUsername: string | null;
  conversationMeta: ConversationThreadTweet['_conversationMeta'];
  createdAt: string | null;
  hasMedia: boolean;
  id: string;
  metrics: {
    likeCount: number;
    retweetCount: number;
    replyCount: number;
    quoteCount: number;
    bookmarkCount: number | null;
    impressionCount: number | null;
  };
  replyToTweetId: string | null;
  text: string | null;
}
interface ConversationSummaryChat {
  canChat: boolean;
  copySummary: () => Promise<void>;
  handleChat: () => void;
  isChatEnabled: boolean;
  isChatLoading: boolean;
}
export function useConversationSummaryChat({
  canonicalSummaryTweetId,
  summaryText,
  summaryImages,
  thread,
  resolvedConsideredTweetCount,
  resolvedImageCount,
  totalTweetCount,
  conversation,
  rootProfile
}: ConversationSummaryChatParameters): ConversationSummaryChat {
  const {
    openChat,
    registerContext,
    appendMessages,
    updateMessage,
    isEnabled: isChatEnabled
  } = useUniversalChat();
  const [isChatLoading, setIsChatLoading] = useState(false);
  const copySummary = useCallback(async () => {
    const trimmed = toTrimmedNonEmptyString(summaryText);
    if (!trimmed) {
      return;
    }
    let capturedError: unknown = null;
    await copyToClipboard(trimmed, {
      onError: error => {
        capturedError = error ?? new Error('Clipboard is unavailable in this browser.');
      }
    });
    if (capturedError) {
      throw resolveClipboardError(capturedError);
    }
  }, [summaryText]);
  const handleChat = useCallback(() => {
    if (!isChatEnabled || isChatLoading || !toTrimmedNonEmptyString(summaryText)) {
      return;
    }
    setIsChatLoading(true);
    const timestamp = Date.now();
    const progressMessageId = `conversation-summary-progress-${canonicalSummaryTweetId}-${timestamp}`;
    const progressTimestamp = new Date();
    openChat();
    appendMessages([{
      id: progressMessageId,
      role: 'assistant',
      content: 'Preparing conversation context…',
      timestamp: progressTimestamp,
      status: 'complete'
    }]);
    try {
      const conversationTitle = rootProfile?.username ? `Conversation with @${rootProfile.username}` : `Conversation ${conversation.id}`;
      const tweetsForContext: ConversationContextTweet[] = [];
      for (const id of thread.tweetIds.slice(0, MAX_TWEETS_FOR_CHAT_CONTEXT)) {
        const tweet = thread.tweets[id];
        if (!tweet) {
          continue;
        }
        const profile = tweet.authorId && thread.profiles ? thread.profiles[tweet.authorId] : undefined;
        const replyReference = tweet.referencedTweets?.find(reference => reference?.type === 'repliedTo' && typeof reference?.id === 'string');
        tweetsForContext.push({
          id: tweet.id,
          text: tweet.text ?? null,
          createdAt: toIsoOrNull(tweet.createdAt),
          authorId: tweet.authorId ?? null,
          authorUsername: profile?.username ?? tweet.author?.username ?? null,
          authorName: profile?.name ?? tweet.author?.name ?? null,
          metrics: {
            likeCount: tweet.likeCount ?? 0,
            retweetCount: tweet.retweetCount ?? 0,
            replyCount: tweet.replyCount ?? 0,
            quoteCount: tweet.quoteCount ?? 0,
            bookmarkCount: tweet.bookmarkCount ?? null,
            impressionCount: tweet.impressionCount ?? null
          },
          conversationMeta: tweet._conversationMeta,
          replyToTweetId: replyReference?.id ?? null,
          hasMedia: Array.isArray(tweet.mediaItems) && tweet.mediaItems.length > 0
        });
      }
      const authorIds = new Set<string>();
      for (const tweet_0 of tweetsForContext) {
        if (tweet_0.authorId) {
          authorIds.add(tweet_0.authorId);
        }
      }
      const participants = [...authorIds].map(authorId => {
        const profile_0 = thread.profiles?.[authorId];
        return {
          id: authorId,
          username: profile_0?.username ?? null,
          name: profile_0?.name ?? null,
          profileImageUrl: profile_0?.profileImageUrl ?? null
        };
      });
      const summaryPreparedAt = progressTimestamp.toISOString();
      registerContext({
        source: `conversation-summary-${canonicalSummaryTweetId}`,
        title: conversationTitle,
        data: {
          conversation: {
            id: conversation.id,
            rootTweetId: conversation.rootTweetId,
            tweetCount: conversation.tweetCount,
            participantCount: conversation.participantCount,
            maxDepth: conversation.maxDepth,
            lastActivityAt: toIsoOrNull(conversation.lastActivityAt),
            dataSources: conversation.dataSources,
            isComplete: conversation.isComplete,
            missingTweets: Boolean(conversation.missingTweets),
            consideredTweets: resolvedConsideredTweetCount,
            includedImages: resolvedImageCount,
            totalAvailableTweets: totalTweetCount,
            tweetsIncludedInContext: tweetsForContext.length,
            contextTweetLimit: MAX_TWEETS_FOR_CHAT_CONTEXT,
            summaryPreparedAt
          },
          tweets: tweetsForContext,
          participants,
          summaryImages: summaryImages.map(image => ({
            url: image.url,
            altText: image.altText ?? null
          }))
        },
        summary: summaryText
      });
      updateMessage({
        id: progressMessageId,
        role: 'assistant',
        content: 'Conversation context ready. Ask follow-up questions about this thread.',
        timestamp: new Date(),
        status: 'complete'
      });
      const instructionTimestamp = new Date();
      const instructionMessage: Message = {
        id: `conversation-summary-instructions-${canonicalSummaryTweetId}-${timestamp}`,
        role: 'system',
        content: ['You are helping the user explore the Twitter conversation provided in the context above.', 'Use the registered conversation data and summary to answer follow-up questions with grounded, factual responses.', 'Offer 2-3 concise example questions the user could ask about this conversation.', 'Only list the questions as bullet points and do not answer them.'].join('\n'),
        timestamp: instructionTimestamp,
        visibility: 'hidden'
      };
      const exampleRequest: Message = {
        id: `conversation-summary-examples-${canonicalSummaryTweetId}-${timestamp}`,
        role: 'user',
        content: 'Suggest example questions I could ask about this conversation.',
        timestamp: new Date(instructionTimestamp.getTime() + 1),
        visibility: 'hidden'
      };
      appendMessages([instructionMessage, exampleRequest]);
    } catch (error_0) {
      const message = error_0 instanceof Error ? error_0.message : 'Unable to open chat for this conversation.';
      updateMessage({
        id: progressMessageId,
        role: 'assistant',
        content: message,
        timestamp: new Date(),
        status: 'complete'
      });
    } finally {
      setIsChatLoading(false);
    }
  }, [appendMessages, canonicalSummaryTweetId, conversation, isChatEnabled, isChatLoading, openChat, registerContext, resolvedConsideredTweetCount, resolvedImageCount, rootProfile?.username, summaryImages, summaryText, thread, totalTweetCount, updateMessage]);
  const canChat = useMemo(() => isChatEnabled && !isChatLoading && Boolean(toTrimmedNonEmptyString(summaryText)), [isChatEnabled, isChatLoading, summaryText]);
  return {
    canChat,
    isChatLoading,
    isChatEnabled,
    handleChat,
    copySummary
  };
}
function resolveClipboardError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  return new Error(String(error));
}